import { Check, CornerDownLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { FlaggedIssue } from '../types';

interface KpiQuadrantProps {
    issues: FlaggedIssue[];
    hasRdtReport?: boolean;
    hasRldtReport?: boolean;
    partDescription?: string;
    onPrefillInput?: (value: string) => void;
}

type EngineStatus = 'has_issues' | 'no_issues' | 'no_data';

interface EngineBucket {
    engine: string;
    label: string;
    issues: FlaggedIssue[];
    status: EngineStatus;
}

interface Category {
    id: string;
    label: string;
    engines: EngineBucket[];
}

interface Segment {
    id: string;
    label: string;
    categories: Category[];
}

const TREE_CONFIG = [
    {
        id: 'wholesale',
        label: 'Wholesale (Depot)',
        categories: [
            { id: 'wholesale_demand', section: 'COST - DEMAND PLANNING', label: 'Cost - Demand' },
            { id: 'wholesale_supply', section: 'RELIABILITY - COMPONENT OVERHAUL', label: 'Reliability - Production' },
        ],
    },
    {
        id: 'retail',
        label: 'Retail (Field, Bases)',
        categories: [
            { id: 'retail_supply', section: 'AVAILABILITY - LOGISTICS & INVENTORY', label: 'Availability - Allocation' },
            { id: 'retail_demand', section: 'MAINTAINABILITY - LINE & BASE MAINTENANCE', label: 'Maintainability - Line Mx' },
        ],
    },
];

const KPI_NAME_MAP: Record<string, string> = {
    'Total Awaiting Part Rate': 'Total AOG Rate',
    'Relative Awaiting Part Rate': 'Relative AOG Rate',
    'Cross-Station Part Availability Risk': 'Cross-Station Risk',
    'Part Availability Risk Trend': 'Availability Risk Trend',
    'Part Removal Rate (1000/MUBR)': 'Removal Rate',
    'Total Part Demand': 'Part Demand',
    'Depot Production Increase Needed': 'Depot Production Increase',
    'Extra Usable Spares Needed': 'Extra Spares Needed',
    'Improvement by Reallocating Station Spares': 'Reallocation of Spares',
};

function normalizeKpi(kpi: string): string {
    return KPI_NAME_MAP[kpi] ?? kpi;
}

const SEVERE_KPIS = new Set([
    'Total AOG Rate',
    'Relative AOG Rate',
    'Cross-Station Risk',
]);

const MEDIUM_KPIS = new Set([
    'Extra Spares Needed',
    'Depot Production Increase',
    'Availability Risk Trend',
    'Removal Rate',
    'Part Demand',
    'Reallocation of Spares',
]);

type IssueSeverity = 'red' | 'yellow';

function getIssueSeverity(issue: FlaggedIssue, globalHasSevere: boolean): IssueSeverity {
    if (issue.report_engine === 'RDT') return 'yellow';
    const canonical = normalizeKpi(issue.kpi);
    if (SEVERE_KPIS.has(canonical)) return 'red';
    if (MEDIUM_KPIS.has(canonical)) return globalHasSevere ? 'red' : 'yellow';
    return 'yellow';
}

const SummaryHint: React.FC<{ label: string; command: string; onPrefill?: (value: string) => void }> = ({ label, command, onPrefill }) => {
    const [activated, setActivated] = useState(false);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(command);
        setActivated(true);
        setTimeout(() => setActivated(false), 2000);
        onPrefill?.(command);
    };

    return (
        <span className="command-hint-wrapper">
            <strong>{label}</strong>
            <button className="command-hint-btn" onClick={handleClick} title={`Stage "${command}" in input`}>
                {activated ? <Check size={12} className="check-icon" /> : <CornerDownLeft size={12} />}
            </button>
        </span>
    );
};

export default function KpiQuadrant({ issues, hasRdtReport, hasRldtReport, partDescription, onPrefillInput }: KpiQuadrantProps) {
    const buildCommand = (label: string) => label;

    const buildEngineCommand = (catLabel: string, engine: string) => `${catLabel} (${engine})`;

    const globalHasSevere = useMemo(
        () => issues.some(i => i.report_engine === 'RLDT' && SEVERE_KPIS.has(normalizeKpi(i.kpi))),
        [issues],
    );

    const effectiveHasRdt = hasRdtReport || issues.some(i => i.report_engine === 'RDT');
    const effectiveHasRldt = hasRldtReport || issues.some(i => i.report_engine === 'RLDT');

    const tree: Segment[] = useMemo(() => {
        return TREE_CONFIG.map(segCfg => {
            const categories: Category[] = segCfg.categories.map(catCfg => {
                const catIssues = issues.filter(i => i.section === catCfg.section);
                const rdtIssues = catIssues.filter(i => i.report_engine === 'RDT');
                const rldtIssues = catIssues.filter(i => i.report_engine === 'RLDT');

                const engines: EngineBucket[] = [
                    {
                        engine: 'RDT',
                        label: 'Mx (RDT)',
                        issues: rdtIssues,
                        status: rdtIssues.length > 0 ? 'has_issues' : (effectiveHasRdt ? 'no_issues' : 'no_data'),
                    },
                    {
                        engine: 'RLDT',
                        label: 'Log (RLDT)',
                        issues: rldtIssues,
                        status: rldtIssues.length > 0 ? 'has_issues' : (effectiveHasRldt ? 'no_issues' : 'no_data'),
                    },
                ];

                return {
                    id: catCfg.id,
                    label: catCfg.label,
                    engines,
                };
            });

            return {
                id: segCfg.id,
                label: segCfg.label,
                categories,
            };
        });
    }, [issues, effectiveHasRdt, effectiveHasRldt]);

    return (
        <div className="kpi-tree">
            {partDescription && (
                <div className="kpi-tree-part-description">
                    <ReactMarkdown>{partDescription}</ReactMarkdown>
                </div>
            )}
            <div className="kpi-tree-title">
                <span>Findings Summary for the Part: </span>
                <SummaryHint
                    label="All Findings"
                    command={buildCommand('All Findings')}
                    onPrefill={onPrefillInput}
                />
            </div>
            {tree.map(segment => {
                const segHasIssues = segment.categories.some(c => c.engines.some(e => e.status === 'has_issues'));
                return (
                <div key={segment.id} className="kpi-tree-segment">
                    <div className="kpi-tree-segment-header">
                        {segHasIssues ? (
                            <SummaryHint
                                label={segment.label}
                                command={buildCommand(segment.label)}
                                onPrefill={onPrefillInput}
                            />
                        ) : (
                            <span>{segment.label}</span>
                        )}
                    </div>

                    {segment.categories.map(cat => {
                        const catHasIssues = cat.engines.some(e => e.status === 'has_issues');
                        return (
                        <div key={cat.id} className="kpi-tree-category">
                            <div className="kpi-tree-category-header">
                                {catHasIssues ? (
                                    <SummaryHint
                                        label={cat.label}
                                        command={buildCommand(cat.label)}
                                        onPrefill={onPrefillInput}
                                    />
                                ) : (
                                    <span>{cat.label}</span>
                                )}
                            </div>

                            <div className="kpi-tree-engines">
                                {cat.engines.map(eng => (
                                    <div key={eng.engine} className="kpi-tree-engine">
                                        {eng.status === 'has_issues' ? (
                                            <>
                                                <SummaryHint
                                                    label={eng.label}
                                                    command={buildEngineCommand(cat.label, eng.engine)}
                                                    onPrefill={onPrefillInput}
                                                />
                                                <span className="kpi-tree-engine-flags">
                                                    {eng.issues.map((issue, i) => {
                                                        const severity = getIssueSeverity(issue, globalHasSevere);
                                                        return (
                                                            <span key={i} title={issue.kpi}>
                                                                <svg
                                                                    className={`kpi-tree-flag ${severity}`}
                                                                    viewBox="0 0 20 16"
                                                                    width="14"
                                                                    height="12"
                                                                    fill="currentColor"
                                                                    aria-label={`${severity} flag: ${issue.kpi}`}
                                                                >
                                                                    <rect x="1" y="0" width="2" height="16" rx="0.5" />
                                                                    <path d="M3 1h13l-4 4 4 4H3V1z" />
                                                                </svg>
                                                            </span>
                                                        );
                                                    })}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="kpi-tree-engine-label">{eng.label}</span>
                                                <span className="kpi-tree-engine-status">{eng.status === 'no_data' ? 'no data' : 'no issues'}</span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ); })}
                </div>
            ); })}
        </div>
    );
}
